import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as common from '../common'
import type * as display from '../display'
import * as dms from '../dms'

import type { Action } from './action'
import type { Clickable } from './clickable'
import type { Page } from './page'

/**
 * Represents a custom component that can be presented to a user. The actual
 * informatation needed to display this {@link Widget} is retrieved using a
 * {@link dms.data.query.Query} (i.e. {@link Widget.query}). That information is returned
 * in the form of a {@link Widget.Rendering}
 */
export interface Widget
  extends common.Identified<Widget.Id>,
    common.Aliased,
    common.Named,
    common.Described.Optionally {
  /**
   * A {@link dms.data.query.Query} associated with this {@link Widget}, used to produce a
   * {@link Widget.Rendering}
   */
  query: Widget.Query
  /**
   * Represents all of the {@link dms.data.query.Variable} that the user can input for
   * this {@link Widget}
   */
  variables: readonly dms.data.query.Variable[]
}
export namespace Widget {
  /**
   * Identifies a {@link Widget}
   */
  export type Id = string
  export namespace Id {
    export const isId: TypeChecker<Id> =
      TypeChecker.isString.usingAlias('mosaic.Widget.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /**
   * Reference to an existing {@link Widget}; e.g., within a {@link Page}
   */
  export interface Ref {
    /**
     * Indicates which {@link Widget} is represented by this {@link Widget.Ref}
     */
    // NOTE: this naming convention tries to match Action.Ref
    widget: common.FlexibleIdentifier<Id>
    variables?: dms.data.query.Variable.Lookup
  }

  /**
   * @see {@link Widget.query}
   */
  export type Query = dms.data.query.ForSingleton<Query.Result>
  export namespace Query {
    // NOTE: we don't really have a way to check if the query is a valid query
    // for a widget rendering, so we just check if it is a query in general
    export const isQuery = dms.data.query.Query.isQuery as TypeChecker<Query>
    export const assertIsQuery: TypeAsserter<Query> =
      TypeAsserter.Factory.from(isQuery)

    /**
     * Return type of a {@link Widget.Query}
     *
     * @see {@link Widget.Rendering}
     */
    export type Result = Omit.FromUnion<Widget.Rendering, 'id' | 'variables'>
  }

  /**
   * Represents the actual information to show to a user when they see a
   * {@link Widget} on a specific {@link Page}, in a specific context, at a
   * specific point in time
   */
  // NOTE: this is a discriminated union
  export type Rendering<TType extends Type = Type> = TType extends any
    ? // NOTE: everything is optional so we are forced to handle it
      Partial<
        common.Identified<Id> &
          common.Aliased &
          common.Named &
          // A summary widget can be linked to another dashboard and support
          // executing various actions from its source dashboard
          Clickable.Optionally & {
            // TODO: should type go here or in display?
            type: TType
            content: Content<TType>
            variables: readonly dms.data.query.Variable[]
            /**
             * User-accessible actions for this {@link Widget}.
             *
             * Single action appear as button.
             * Multiple actions are consolidated into an overflow menu.
             *
             * @see {@link Action.Ref.WithIcon}
             */
            overflowActions: readonly Action.Ref.WithIcon[]
            /**
             * When any {@link Action} that is referenced here is successfully
             * executed, this widget will be re-rendered
             *
             * For example, if you have a table and an action that adds data to
             * that table, you may want to have the table "watch" that action so
             * that when the action is executed, the table is re-rendered to
             * show that new data
             */
            watch: readonly common.FlexibleIdentifier<Action.Id>[]
          }
      >
    : never
  export namespace Rendering {
    export type AiResult = Pick<Widget.Rendering, 'content' | 'type' | 'name'>
  }

  /**
   * Determines the structure and display of this {@link Content}
   *
   * Look at the implementation of {@link Content} or {@link Content.Data} to
   * learn more about the specifics of a given {@link Type}
   */
  export enum Type {
    Table = 'table',
    Details = 'details',
    Questions = 'questions',
    AdaptiveCard = 'adaptive-card',
    ReportCard = 'report-card',
    Metric = 'metric',
    ScoreCard = 'score-card',
    Charts = 'charts',
    TreeEditor = 'tree-editor',
    ComparisonTable = 'comparison-table',
    TreeTable = 'tree-table',
  }
  // Needs to be before namespace is defined
  const TYPES: ReadonlySet<Type> = new Set(Object.values(Type) as Type[])
  export namespace Type {
    export const ALL: readonly Type[] = Array.from(TYPES)
    export const isType = TypeChecker.Factory.oneOf(TYPES)
    export const assertIsType: TypeAsserter<Type> =
      TypeAsserter.Factory.from(isType)

    export type SupportsAiGeneration = Exclude<
      Type,
      | Type.Table
      | Type.Questions
      | Type.AdaptiveCard
      | Type.TreeEditor
      | Type.TreeTable
    >
    export namespace SupportsAiGeneration {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export const ALL = Array.from(
        new Set<SupportsAiGeneration>([
          Type.Details,
          Type.ReportCard,
          Type.Metric,
          Type.Charts,
          Type.ComparisonTable,
          Type.ScoreCard,
        ]),
      )
    }

    export function getAiDescription(type: SupportsAiGeneration): string {
      switch (type) {
        case Type.Details:
          return 'a list of field/value pairs describing a single entity'
        case Type.ReportCard:
          return 'a set of grouped metrics in sections, each with a name and description'
        case Type.Metric:
          return 'a single key performance indicator (KPI), optionally with a trend indicator'
        case Type.Charts:
          return 'data visualizations such as line, bar, stacked bar, donut, scatter, or sunburst charts'
        case Type.ComparisonTable:
          return 'a side-by-side comparison of multiple items across common criteria with structured values'
        case Type.ScoreCard:
          return 'a side-by-side comparison of options across several evaluation criteria, each with qualitative and quantitative metrics'
      }
    }
  }

  /**
   * Each {@link Content} is attempting to display some data. This indicates what
   * that data should look like, based on the given {@link Type}
   */
  // NOTE: all properties in here (including any nested types) should be
  // optional so that we handle it, in case a query returns undefined for some
  // properties. We could use Partial.Deep somewhere in this file, but it is
  // better if the types themselves make everything optional, as that will
  // ensure everything that uses those types handles undefined values
  export type Content<TType extends Type = Type> = {
    /**
     * @see {@link https://adaptivecards.io/explorer/}
     *
     * TODO: maybe convert this to a TS type
     * {@link http://adaptivecards.io/schemas/adaptive-card.json}
     */
    [Type.AdaptiveCard]: unknown
    /**
     * @see {@link display.chart.Chart.Collection}
     */
    [Type.Charts]: display.chart.Chart.Collection
    /**
     * @see {@link display.Field.Collection}
     */
    [Type.Details]: display.Field.Collection
    /**
     * Each {@link display.Metric.Group} represents a section
     *
     * Each {@link display.Metric} represents a row of that section
     */
    [Type.Metric]: display.Metric
    /**
     * @see {@link display.Field.Collection}
     */
    [Type.Questions]: display.Field.Collection
    /**
     * Each {@link display.Metric.Group} represents a section
     *
     * Each {@link display.Metric} represents a row of that section
     */
    [Type.ReportCard]: display.Metric.Group.Series
    /**
     * /**
     * Each {@link display.Metric.Group} represents a section
     *
     * Each {@link display.Metric} represents a row of that section
     */
    [Type.ScoreCard]: display.Metric.Group.Series
    /**
     * @see {@link display.Table}
     */
    [Type.Table]: display.Table
    /**
     * @see {@link display.tree.Tree}
     */
    [Type.TreeEditor]: display.tree.Tree
    /**
     * @see {@link display.ComparisonTable}
     */
    [Type.ComparisonTable]: display.ComparisonTable
    /**
     * @see {@link display.Table.Tree}
     */
    [Type.TreeTable]: display.Table.Tree
  }[TType]

  export namespace Content {
    // TODO: This exists only so we can reference `content.onSave` as a property.
    // `onSave` should be moved to its own type with a dedicated type checker,
    // allowing validation like (TypeChecker)isOnSaveAction(content)
    // NOTE:  We exclude Type.AdaptiveCard from FlattenUnion because its content
    // type is 'unknown', which would make the entire flattened union type
    // 'unknown' as well
    export type Flattened = FlattenUnion<
      Content<Exclude<Type, Type.AdaptiveCard>>
    >
  }

  /**
   * Information needed to create a {@link Widget}
   */
  export type Input = Partial.Semi<Omit<Widget, 'id'>, 'variables'>

  /**
   * Information needed to update a {@link Widget}
   */
  export type Updates = Partial<Input>
}
